import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createProjects,
  deleteProjectImages,
  getProjectsByID,
  ProjectsImageUpload,
  updateProjects,
} from "./services/projects.services";
import { clearProjectsDetails } from "./projectsSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import FormAttachmentUpload from "../../components/formFields/FormAttachmentUpload";
import { MdImageNotSupported } from "react-icons/md";

const CreateProjects: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ProjectID = location.state?.ProjectID;
  const ProjectsDetail = useAppSelector(
    (state) => state.projects.ProjectsDetail
  );
  const loading = useAppSelector((state) => state.projects.loading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (ProjectID) {
      dispatch(getProjectsByID(ProjectID));
    }
    return () => {
      dispatch(clearProjectsDetails());
    };
  }, [ProjectID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ProjectsDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      ProjectTitle: values?.ProjectTitle ? values.ProjectTitle : "",
      Description: values?.Description ? values.Description : "",
      ProjectURL: values?.ProjectURL ? values.ProjectURL : "",
      MetaTitle: values?.MetaTitle ? values.MetaTitle : "",
      MetaKeyword: values?.MetaKeyword ? values.MetaKeyword : "",
      MetaDescription: values?.MetaDescription ? values.MetaDescription : "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const MainImage =
      values?.MainImage &&
      typeof values?.MainImage !== "string" &&
      Array.isArray(values?.MainImage) &&
      values?.MainImage?.length > 0
        ? values.MainImage[0].getRawFile()
        : "";
    try {
      if (ProjectID) {
        const editPayload = {
          ...formData,
          ID: ProjectID,
        };
        const response = await dispatch(updateProjects(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.Images &&
              values.Images.length > 0 &&
              typeof values.Images !== "string") ||
            (values.MainImage &&
              typeof values.MainImage !== "string" &&
              ProjectID)
          ) {
            const Images = values.Images?.map((fileItem: any) =>
              fileItem.getRawFile()
            );

            Images?.map((image: any) => ImageData.append("InnerImages", image));
            ImageData.append("MainImage", MainImage);
            ImageData.append("ID", ProjectID);
            await dispatch(ProjectsImageUpload(ImageData));
          }
          navigate("/projects");
        }
      } else {
        const response = await dispatch(createProjects(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.Images &&
              values.Images.length > 0 &&
              typeof values.Images !== "string") ||
            (values.MainImage &&
              typeof values.MainImage !== "string" &&
              response?.payload?.Data?.ID)
          ) {
            const Images = values.Images?.map((fileItem: any) =>
              fileItem.getRawFile()
            );
            Images?.map((image: any) => ImageData.append("InnerImages", image));
            ImageData.append("MainImage", MainImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(ProjectsImageUpload(ImageData));
          }
          navigate("/projects");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleDeleteImage = async (ID: number) => {
    const res = await dispatch(deleteProjectImages(ID));
    if (res.meta.requestStatus === "fulfilled") {
      if (ProjectID) {
        dispatch(getProjectsByID(ProjectID));
      }
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={ProjectsDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 40 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {ProjectID ? "Update Project" : "Create Project"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="ProjectTitle"
                      label="Title"
                      placeholder="Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="ProjectURL"
                      label="Project URL"
                      placeholder="Projects URL"
                      component={FormTextField}
                    />
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      justifyContent="start"
                      name={`MainImage`}
                      label="Main Image"
                      type="file"
                      accept="image/*"
                      alternateName="Image"
                      style={{ height: 200 }}
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    {/* <Field
                      name="Description"
                      label="Description"
                      placeholder="Description"
                      component={FormTextArea}
                      astrike={true}
                      validator={requiredValidator}
                      style={{ height: 130 }}
                    /> */}
                    <Field
                      name="Description"
                      label="Description"
                      component={FormRichTextField}
                      style={{ height: 380 }}
                      astrike={true}
                      validator={requiredValidator}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={2} style={{ padding: "20px 0" }}>
                    <Field
                      name={"Images"}
                      label="Upload Images"
                      type={"file"}
                      accept="image/*"
                      multiple={true}
                      component={FormAttachmentUpload}
                      //  validator={requiredValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={2}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <>
                      {(!ProjectsDetail?.InnerImages ||
                        ProjectsDetail.InnerImages.length === 0) && (
                        <div
                          className="text-center"
                          style={{ color: "#6c757d" }}
                        >
                          <span className="k-icon k-i-image k-icon-xl mb-2">
                            <MdImageNotSupported style={{ fontSize: "24px" }} />
                          </span>
                          <p style={{ color: "#f17272" }} className="mb-0">
                            No images uploaded yet
                          </p>
                        </div>
                      )}
                    </>
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={4}>
                    <div className="card shadow-sm border-0 mb-4">
                      {ProjectsDetail?.InnerImages?.length > 0 && (
                        <div
                          className="card-body p-3"
                          style={{
                            background: "#f8f9fa",
                            borderRadius: "8px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5
                              className="mb-0"
                              style={{
                                color: "#2c3e50",
                                fontWeight: "600",
                                fontSize: "1.1rem",
                              }}
                            >
                              Uploaded Images
                            </h5>
                            <span className="badge bg-primary rounded-pill">
                              {ProjectsDetail?.InnerImages?.length || 0} files
                            </span>
                          </div>

                          <div className="d-flex flex-wrap gap-3">
                            {ProjectsDetail?.InnerImages?.map((image: any) => (
                              <div
                                key={image.ID}
                                className="position-relative"
                                style={{
                                  width: "120px",
                                  transition: "all 0.3s ease",
                                }}
                              >
                                <div className="card h-100 border-0 shadow-sm overflow-hidden">
                                  <div className="ratio ratio-1x1 bg-light overflow-hidden">
                                    <img
                                      className="img-fluid object-fit-cover"
                                      src={image.ImagePath}
                                      alt="Uploaded document"
                                      style={{
                                        cursor: "pointer",
                                        transition: "transform 0.3s",
                                      }}
                                      onMouseEnter={(e) =>
                                        (e.currentTarget.style.transform =
                                          "scale(1.05)")
                                      }
                                      onMouseLeave={(e) =>
                                        (e.currentTarget.style.transform =
                                          "scale(1)")
                                      }
                                      onClick={() =>
                                        window.open(
                                          `${image.ImagePath}`,
                                          "_blank"
                                        )
                                      }
                                    />
                                  </div>
                                  <div className="card-footer p-2 bg-white border-0">
                                    <Button
                                      className="w-100"
                                      size="small"
                                      themeColor="error"
                                      fillMode="outline"
                                      rounded="medium"
                                      style={{
                                        border: "1px solid #dc3545",
                                        fontWeight: "500",
                                      }}
                                      onClick={() =>
                                        handleDeleteImage(image.ID)
                                      }
                                    >
                                      <span className="k-icon k-i-trash me-1"></span>
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4} style={{ width: "100%" }}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      component={FormTextField}
                    />
                    <Field
                      name="MetaKeyword"
                      label="Meta Keywords"
                      component={FormTextField}
                      rows={1}
                    />
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      component={FormTextArea}
                      style={{ height: 130 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={ProjectID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/projects")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateProjects;
